/**
 * Sorumluluk Kuralları Detay Sayfası — Faz 29
 * ==============================================
 * Ekip × WBS Kapsamı × Lokasyon Kapsamı × Öncelik kuralları.
 * Kural değişikliği Work Item'lara otomatik yansır.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UsersFour, Plus, PencilSimple, TrashSimple, ArrowLeft,
  TreeStructure, MapTrifold, CheckCircle, WarningCircle, GridFour, Warning, FilePdf,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

function TreePicker({ nodes, selected, onToggle, testIdPrefix, emptyLabel }) {
  if (!nodes.length) return <div className="text-[11px] text-slate-500 italic p-3">{emptyLabel}</div>;
  const byParent = {};
  for (const n of nodes) byParent[n.parent_id || ""] = [...(byParent[n.parent_id || ""] || []), n];
  const render = (parentId, depth) => (
    (byParent[parentId] || []).map((n) => (
      <div key={n.id}>
        <div className="flex items-center gap-1.5 hover:bg-slate-50 rounded-sm px-1"
             style={{ paddingLeft: 6 + depth * 12 }}>
          <input type="checkbox"
                 data-testid={`${testIdPrefix}-${n.id}`}
                 checked={selected.includes(n.id)}
                 onChange={() => onToggle(n.id)}
                 className="w-3.5 h-3.5" />
          <span className="text-xs text-slate-700 truncate">{n.name}</span>
          {n.category && (
            <span className="text-[8px] uppercase tracking-widest font-bold bg-slate-100 text-slate-500 px-1 rounded-sm">
              {n.category}
            </span>
          )}
        </div>
        {render(n.id, depth + 1)}
      </div>
    ))
  );
  return <div className="text-xs">{render("", 0)}</div>;
}

const emptyForm = {
  team_id: "", wbs_scope_ids: [], location_scope_ids: [],
  priority: 1, active: true, note: "",
};

export default function ResponsibilityRules() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const canManage = !!user;
  const confirm = useConfirm();

  const [site, setSite] = useState(null);
  const [rules, setRules] = useState([]);
  const [wbsTree, setWbsTree] = useState([]);
  const [phases, setPhases] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Toplu silme
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const toggleSelectAll = () => setSelectedIds((prev) => {
    if (prev.size === rules.length && rules.length > 0) return new Set();
    return new Set(rules.map((r) => r.id));
  });

  // Görsel matris — ekip filtresi
  const [matrixTeamFilter, setMatrixTeamFilter] = useState("all");

  // Görsel matris hesaplaması: WBS leaves × Location leaves
  const matrix = useMemo(() => {
    if (!wbsTree.length || !phases.length) return null;
    // Leaf tespit
    const wbsChildOf = {};
    for (const n of wbsTree) {
      const pid = n.parent_id || "";
      wbsChildOf[pid] = [...(wbsChildOf[pid] || []), n.id];
    }
    const wbsLeaves = wbsTree.filter((n) => !wbsChildOf[n.id]);
    const locChildOf = {};
    for (const n of phases) {
      const pid = n.parent_id || "";
      locChildOf[pid] = [...(locChildOf[pid] || []), n.id];
    }
    const locLeaves = phases.filter((n) => !locChildOf[n.id]);

    // Ata id listesi (kendi + üstler)
    const wbsParentOf = Object.fromEntries(wbsTree.map((n) => [n.id, n.parent_id || ""]));
    const locParentOf = Object.fromEntries(phases.map((n) => [n.id, n.parent_id || ""]));
    const ancWbs = (id) => {
      const out = [id]; let cur = wbsParentOf[id];
      let s = 0; while (cur && s++ < 30) { out.push(cur); cur = wbsParentOf[cur]; }
      return out;
    };
    const ancLoc = (id) => {
      const out = [id]; let cur = locParentOf[id];
      let s = 0; while (cur && s++ < 30) { out.push(cur); cur = locParentOf[cur]; }
      return out;
    };

    // Ekip renk paleti
    const palette = [
      "#1B4776", "#95C11F", "#f97316", "#8b5cf6", "#ec4899",
      "#14b8a6", "#eab308", "#ef4444", "#6366f1", "#84cc16",
      "#06b6d4", "#f59e0b", "#a855f7", "#22c55e", "#f43f5e",
    ];
    const teamColorMap = {};
    let ci = 0;
    for (const t of teams) { teamColorMap[t.id] = palette[ci % palette.length]; ci++; }

    const activeRules = rules.filter((r) => r.active);
    const filteredRules = matrixTeamFilter === "all"
      ? activeRules
      : activeRules.filter((r) => r.team_id === matrixTeamFilter);

    // Cell: (wbsLeaf, locLeaf) → matching rules
    const cells = {};
    for (const wl of wbsLeaves) {
      const wAncSet = new Set(ancWbs(wl.id));
      for (const ll of locLeaves) {
        const lAncSet = new Set(ancLoc(ll.id));
        const matchedRules = filteredRules.filter((r) => {
          const wScope = r.wbs_scope_ids || [];
          const lScope = r.location_scope_ids || [];
          const wOK = wScope.length === 0 || wScope.some((id) => wAncSet.has(id));
          const lOK = lScope.length === 0 || lScope.some((id) => lAncSet.has(id));
          return wOK && lOK;
        });
        const key = `${wl.id}|${ll.id}`;
        const teamsInCell = [...new Set(matchedRules.map((r) => r.team_id))];
        cells[key] = {
          rules: matchedRules,
          teams: teamsInCell,
          conflict: teamsInCell.length > 1,
        };
      }
    }
    return { wbsLeaves, locLeaves, cells, teamColorMap };
  }, [wbsTree, phases, rules, teams, matrixTeamFilter]);

  const conflictCount = matrix
    ? Object.values(matrix.cells).filter((c) => c.conflict).length : 0;
  const coveredCount = matrix
    ? Object.values(matrix.cells).filter((c) => c.teams.length > 0).length : 0;
  const totalCells = matrix
    ? matrix.wbsLeaves.length * matrix.locLeaves.length : 0;

  const { sort, toggleSort, sortedRows: sortedRules } = useSortableRows(rules, {
    defaultKey: "team_name", defaultDir: "asc",
    getters: {
      team_name: (r) => r.team_name || r.team_code || "",
      wbs_scope: (r) => (r.wbs_scope_labels || []).join(", "),
      loc_scope: (r) => (r.loc_scope_labels || []).join(", "),
      active: (r) => (r.active ? 1 : 0),
      notes: (r) => r.notes || "",
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rR, wbsR, phR, tR, sR] = await Promise.all([
        axios.get(`${API}/responsibility-rules`, { params: { site_id: siteId } }),
        axios.get(`${API}/sites/${siteId}/wbs-nodes`),
        axios.get(`${API}/sites/${siteId}/phases`),
        axios.get(`${API}/teams`, { params: { site_id: siteId } }),
        axios.get(`${API}/sites/${siteId}/project`),
      ]);
      setRules(rR.data || []);
      setSelectedIds(new Set());
      setWbsTree((wbsR.data || []).map((n) => ({
        id: n.id, name: n.name, parent_id: n.parent_id || "", category: n.category,
      })));
      setPhases((phR.data || []).map((p) => ({
        id: p.id, name: p.name, parent_id: p.parent_id || "", category: p.category || "Etap",
      })));
      setTeams(tR.data || []);
      setSite({ id: siteId, name: sR.data.site_name, code: sR.data.site_code });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null); setForm({ ...emptyForm }); setDlgOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      team_id: r.team_id,
      wbs_scope_ids: r.wbs_scope_ids || [],
      location_scope_ids: r.location_scope_ids || [],
      priority: r.priority || 1, active: r.active, note: r.note || "",
    });
    setDlgOpen(true);
  };
  const save = async () => {
    if (!form.team_id) return toast.error("Ekip seçiniz");
    try {
      const payload = { ...form, site_id: siteId, priority: Number(form.priority) || 1 };
      if (editing) {
        await axios.put(`${API}/responsibility-rules/${editing.id}`, payload);
        toast.success("Kural güncellendi ve uygulandı");
      } else {
        await axios.post(`${API}/responsibility-rules`, payload);
        toast.success("Kural eklendi ve uygulandı");
      }
      setDlgOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (r) => {
    const ok = await confirm({
      title: "Kural Sil",
      description: `${r.team_name} ekibinin kuralı silinsin mi?`,
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/responsibility-rules/${r.id}`);
      toast.success("Silindi ve yeniden atama yapıldı");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirm({
      title: "Seçili Kuralları Sil",
      description: `${selectedIds.size} kural silinsin mi? Bu işlem geri alınamaz ve etkilenen Work Item'lardaki otomatik ekip atamaları yeniden hesaplanır.`,
      confirmText: `Evet, ${selectedIds.size} Kuralı Sil`, variant: "destructive",
    });
    if (!ok) return;
    try {
      const { data } = await axios.post(
        `${API}/responsibility-rules/bulk-delete`,
        { ids: Array.from(selectedIds) },
      );
      toast.success(`${data.deleted} kural silindi ve yeniden atama yapıldı`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Kapsam etiketi
  const scopeLabel = (ids, nodes) => {
    if (!ids || !ids.length) return "Tümü";
    const nameMap = Object.fromEntries(nodes.map((n) => [n.id, n.name]));
    const names = ids.map((i) => nameMap[i]).filter(Boolean);
    if (names.length <= 2) return names.join(" · ");
    return `${names.slice(0, 2).join(" · ")} +${names.length - 2}`;
  };

  return (
    <Shell
      title={site ? `${site.name} — Sorumluluk Kuralları` : "Yükleniyor…"}
      subtitle="Proje Yönetimi › Sorumluluk Kuralları"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline"
                  onClick={() => window.open(`${API}/responsibility-rules/export.pdf?site_id=${siteId}`, "_blank")}
                  data-testid="rr-export-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9">
            <FilePdf size={14} className="mr-1.5 text-red-600" /> PDF Aktar
          </Button>
          {canManage && selectedIds.size > 0 && (
            <Button onClick={bulkDelete} data-testid="rr-bulk-delete"
                    variant="destructive"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <TrashSimple size={14} className="mr-1.5" />
              Seçili {selectedIds.size} Kuralı Sil
            </Button>
          )}
          {canManage && (
            <Button onClick={openNew} data-testid="rr-new"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white h-9 text-xs uppercase tracking-widest">
              <Plus size={14} className="mr-1.5" /> Yeni Kural
            </Button>
          )}
          <Link to="/proje/sorumluluk-kurallari">
            <Button variant="outline" className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ArrowLeft size={14} className="mr-1.5" /> Şantiye Listesi
            </Button>
          </Link>
        </div>
      }
    >
      {/* Görsel Kapsam Matrisi — Faz 29.3 */}
      {matrix && matrix.wbsLeaves.length > 0 && matrix.locLeaves.length > 0 && (
        <Card className="rounded-none sm:rounded-sm overflow-hidden mb-3" data-testid="rr-matrix-card">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-slate-50 flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
              <GridFour size={14} weight="duotone" className="text-[#1B4776]" />
              Görsel Kapsam Haritası — WBS × Lokasyon × Ekip
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
                <span className="text-slate-500">Toplam Hücre:</span>
                <span className="font-mono text-slate-900">{totalCells}</span>
                <span className="text-emerald-600 flex items-center gap-1">
                  <CheckCircle size={11} weight="fill" /> {coveredCount} Kapsanan
                </span>
                {conflictCount > 0 && (
                  <span className="text-rose-600 flex items-center gap-1">
                    <Warning size={11} weight="fill" /> {conflictCount} Çakışan
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ekip</Label>
                <Select value={matrixTeamFilter} onValueChange={setMatrixTeamFilter}>
                  <SelectTrigger data-testid="rr-matrix-team-filter"
                                 className="h-8 rounded-none sm:rounded-sm w-48 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Ekipler</SelectItem>
                    {teams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-sm"
                                style={{ background: matrix.teamColorMap[t.id] || "#cbd5e1" }} />
                          {t.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Ekip renk lejantı */}
          {teams.length > 0 && (
            <div className="px-3 py-2 border-b border-slate-100 bg-white flex items-center gap-3 flex-wrap">
              <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Ekip Lejantı:</span>
              {teams.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 text-[10px] text-slate-700">
                  <span className="w-2.5 h-2.5 rounded-sm border border-white shadow-sm"
                        style={{ background: matrix.teamColorMap[t.id] }} />
                  {t.name}
                </span>
              ))}
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 ml-auto">
                <span className="w-2.5 h-2.5 rounded-sm bg-slate-100 border border-slate-300" />
                Ekipsiz
              </span>
            </div>
          )}

          <div className="overflow-auto" style={{ maxHeight: 500 }}>
            <table className="border-collapse text-[10px]" data-testid="rr-matrix-table">
              <thead>
                <tr className="bg-[#1B4776] text-white sticky top-0 z-10">
                  <th className="text-left px-2 py-2 font-bold sticky left-0 bg-[#1B4776] z-20 border-r border-white/20"
                      style={{ minWidth: 240 }}>
                    <span className="uppercase tracking-widest text-[9px]">WBS Yaprakları ↓ / Lokasyon →</span>
                  </th>
                  {matrix.locLeaves.map((ll) => (
                    <th key={ll.id} className="px-1 py-1 font-semibold border-l border-white/10"
                        style={{ minWidth: 44, width: 44 }}>
                      <div className="rotate-[-60deg] origin-left translate-y-3 whitespace-nowrap text-[10px] font-bold h-14 flex items-end">
                        {ll.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.wbsLeaves.map((wl) => (
                  <tr key={wl.id} className="hover:bg-slate-50/60">
                    <td className="px-2 py-1 border-r border-slate-200 sticky left-0 bg-white z-10 font-semibold text-slate-800 border-b border-slate-100"
                        style={{ minWidth: 240 }}>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block px-1 py-0.5 rounded-sm bg-[#95C11F]/15 text-[8px] uppercase tracking-widest font-bold text-[#5a7a10] flex-shrink-0">
                          {wl.category || "POZ"}
                        </span>
                        <span className="truncate" title={wl.name}>{wl.name}</span>
                      </div>
                    </td>
                    {matrix.locLeaves.map((ll) => {
                      const cell = matrix.cells[`${wl.id}|${ll.id}`];
                      const teamsList = cell?.teams || [];
                      const conflict = cell?.conflict;
                      const title = teamsList.length === 0
                        ? "Ekipsiz"
                        : teamsList.map((tid) =>
                            teams.find((t) => t.id === tid)?.name || tid).join(" ⚠ ");
                      return (
                        <td key={ll.id}
                            data-testid={`rr-cell-${wl.id}-${ll.id}`}
                            title={`${wl.name} × ${ll.name}\n${title}`}
                            className={`border border-slate-100 text-center relative cursor-help ${
                              conflict ? "ring-2 ring-rose-500 z-[5]" : ""
                            }`}
                            style={{ width: 44, height: 28,
                                     background: teamsList.length === 0
                                       ? "#f1f5f9"
                                       : teamsList.length === 1
                                         ? matrix.teamColorMap[teamsList[0]] + "cc"
                                         : `linear-gradient(45deg, ${teamsList.map((t) => matrix.teamColorMap[t] || "#94a3b8").join(", ")})` }}>
                          {conflict && (
                            <Warning size={12} weight="fill"
                                     className="absolute inset-0 m-auto text-white drop-shadow-md" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-2 border-t border-slate-100 text-[10px] text-slate-500 italic bg-slate-50/50">
            İpucu: Hücre üzerine geldiğinizde WBS × Lokasyon × Ekip bilgisi görüntülenir.
            Kırmızı kenarlı hücreler birden fazla ekip tarafından kapsandığını (çakışma) gösterir.
          </div>
        </Card>
      )}

      <Card className="rounded-none sm:rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="rr-table">
            <thead>
              <tr className="bg-[#1B4776] text-white text-[10px] uppercase tracking-widest">
                <th className="text-center px-2 py-2 font-bold w-10">
                  {canManage && rules.length > 0 && (
                    <input type="checkbox"
                           data-testid="rr-select-all"
                           checked={rules.length > 0 && selectedIds.size === rules.length}
                           onChange={toggleSelectAll}
                           className="w-3.5 h-3.5 cursor-pointer" />
                  )}
                </th>
                <SortableTh label="Ekip" k="team_name" sort={sort} onSort={toggleSort} tone="dark" className="text-left px-3 py-2" />
                <SortableTh label="WBS Kapsamı" k="wbs_scope" sort={sort} onSort={toggleSort} tone="dark" className="text-left px-2 py-2" />
                <SortableTh label="Lokasyon Kapsamı" k="loc_scope" sort={sort} onSort={toggleSort} tone="dark" className="text-left px-2 py-2" />
                <SortableTh label="Aktif" k="active" sort={sort} onSort={toggleSort} tone="dark" className="text-center px-2 py-2 w-20" align="center" />
                <SortableTh label="Not" k="notes" sort={sort} onSort={toggleSort} tone="dark" className="text-left px-2 py-2" />
                <th className="text-center px-2 py-2 font-bold w-20">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-500">Yükleniyor…</td></tr>
              ) : sortedRules.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16 text-slate-500">
                  Henüz kural yok. "Yeni Kural" ile başlayın.
                </td></tr>
              ) : sortedRules.map((r, i) => (
                <tr key={r.id} data-testid={`rr-row-${i}`}
                    className={`hover:bg-slate-50 ${selectedIds.has(r.id) ? "bg-blue-50/40" : ""}`}>
                  <td className="text-center px-2 py-2">
                    {canManage && (
                      <input type="checkbox"
                             data-testid={`rr-select-${i}`}
                             checked={selectedIds.has(r.id)}
                             onChange={() => toggleSelect(r.id)}
                             className="w-3.5 h-3.5 cursor-pointer" />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <UsersFour size={12} className="text-[#1B4776]" />
                      <div>
                        <div className="font-bold text-slate-900">{r.team_name || "—"}</div>
                        {r.team_duty && <div className="text-[10px] text-slate-500">{r.team_duty}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-slate-100 text-[10px] text-slate-700">
                      <TreeStructure size={9} />{scopeLabel(r.wbs_scope_ids, wbsTree)}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-blue-50 text-[10px] text-[#1B4776]">
                      <MapTrifold size={9} />{scopeLabel(r.location_scope_ids, phases)}
                    </span>
                  </td>
                  <td className="text-center px-2 py-2">
                    {r.active
                      ? <CheckCircle size={14} className="text-emerald-600 inline" />
                      : <WarningCircle size={14} className="text-slate-400 inline" />}
                  </td>
                  <td className="px-2 py-2 text-slate-600 text-[10px] truncate max-w-xs" title={r.note}>{r.note || "—"}</td>
                  <td className="text-center px-2 py-2">
                    {canManage && (
                      <div className="inline-flex items-center gap-0.5">
                        <button onClick={() => openEdit(r)} data-testid={`rr-edit-${i}`}
                                className="p-1 text-slate-500 hover:text-[#1B4776] hover:bg-slate-100 rounded-sm">
                          <PencilSimple size={12} />
                        </button>
                        <button onClick={() => remove(r)} data-testid={`rr-del-${i}`}
                                className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-sm">
                          <TrashSimple size={12} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-4xl rounded-none sm:rounded-sm" data-testid="rr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersFour size={16} className="text-[#1B4776]" />
              {editing ? "Kural Düzenle" : "Yeni Sorumluluk Kuralı"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Ekip + WBS kapsamı + Lokasyon kapsamı seçin. Boş bırakılan kapsam "tümü" olarak işlenir.
              Aynı kapsam farklı ekibe atanamaz — çakışma varsa sistem uyarır.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ekip *</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="rr-form-team"><SelectValue placeholder="Ekip seçin" /></SelectTrigger>
                <SelectContent>
                  {teams.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-slate-500 text-center">
                      Bu şantiyeye atanmış ekip yok.<br />
                      <span className="text-[10px]">
                        Personel Yönetimi › Ekip Listesi'nden ekip ekleyin.
                      </span>
                    </div>
                  ) : teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.duty ? ` · ${t.duty}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500 mt-1">
                Sadece bu şantiyeye atanmış ekipler listelenir.
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] mb-1 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1">
                  <TreeStructure size={11} /> WBS Kapsamı ({form.wbs_scope_ids.length} seçili)
                </span>
                <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] normal-case tracking-normal font-semibold text-slate-600 hover:text-[#1B4776]"
                       data-testid="rr-wbs-all-label">
                  <input type="checkbox"
                         data-testid="rr-wbs-all"
                         checked={wbsTree.length > 0 && form.wbs_scope_ids.length === wbsTree.length}
                         onChange={(e) => setForm((f) => ({
                           ...f, wbs_scope_ids: e.target.checked ? wbsTree.map((n) => n.id) : [],
                         }))}
                         className="w-3.5 h-3.5" />
                  Tümü
                </label>
              </div>
              <div className="border border-slate-200 rounded-none sm:rounded-sm max-h-72 overflow-y-auto p-1"
                   data-testid="rr-form-wbs">
                <TreePicker nodes={wbsTree} selected={form.wbs_scope_ids}
                            onToggle={(id) => setForm((f) => ({
                              ...f, wbs_scope_ids: f.wbs_scope_ids.includes(id)
                                ? f.wbs_scope_ids.filter((x) => x !== id)
                                : [...f.wbs_scope_ids, id],
                            }))}
                            testIdPrefix="rr-wbs-cb"
                            emptyLabel="WBS boş — Kırınımlar sayfasından ekleyin." />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Boş = Tüm WBS</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] mb-1 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1">
                  <MapTrifold size={11} /> Lokasyon Kapsamı ({form.location_scope_ids.length} seçili)
                </span>
                <label className="inline-flex items-center gap-1 cursor-pointer text-[10px] normal-case tracking-normal font-semibold text-slate-600 hover:text-[#1B4776]"
                       data-testid="rr-loc-all-label">
                  <input type="checkbox"
                         data-testid="rr-loc-all"
                         checked={phases.length > 0 && form.location_scope_ids.length === phases.length}
                         onChange={(e) => setForm((f) => ({
                           ...f, location_scope_ids: e.target.checked ? phases.map((n) => n.id) : [],
                         }))}
                         className="w-3.5 h-3.5" />
                  Tümü
                </label>
              </div>
              <div className="border border-slate-200 rounded-none sm:rounded-sm max-h-72 overflow-y-auto p-1"
                   data-testid="rr-form-loc">
                <TreePicker nodes={phases} selected={form.location_scope_ids}
                            onToggle={(id) => setForm((f) => ({
                              ...f, location_scope_ids: f.location_scope_ids.includes(id)
                                ? f.location_scope_ids.filter((x) => x !== id)
                                : [...f.location_scope_ids, id],
                            }))}
                            testIdPrefix="rr-loc-cb"
                            emptyLabel="Proje Yapısı boş — Etap/Blok tanımlayın." />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Boş = Tüm Lokasyonlar</div>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Aktif</Label>
              <Select value={form.active ? "1" : "0"} onValueChange={(v) => setForm({ ...form, active: v === "1" })}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Evet</SelectItem>
                  <SelectItem value="0">Hayır (pasif)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Not</Label>
              <Textarea value={form.note} rows={2}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        className="rounded-none sm:rounded-sm mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="rr-save"
                    className="bg-[#1B4776] hover:bg-[#153859] text-white">
              {editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
